import GlassTimelineCalendar, { type GlassCalendarEvent } from '@/components/calendar/GlassTimelineCalendar'
import MiniMapPanel from '@/features/charts/components/MiniMapPanel'
import { MapRouteShell } from '@/app/shell/map-route-shell'
import { useStormEventData } from '@/domains/events/hooks/useEventDerivedData'
import type { SensorEventSummary } from '@/domains/events/selectors'
import { parseSearch } from '@/app/router/search-params'
import { depthToColor } from '@/features/map/layer-style'
import { Box, Button, Paper, Stack, Typography } from '@mui/material'
import { createFileRoute } from '@tanstack/react-router'
import { format } from 'date-fns'
import React from 'react'

type StormEventMetadata = {
  cityEventId: number
  sensorEvents: SensorEventSummary[]
}

type CalendarEventItem = GlassCalendarEvent & {
  kind: 'storm'
  maxDepthInches?: number
  sensorEventCount?: number
  sensorCount?: number
  metadata?: StormEventMetadata
}

const MS_PER_DAY = 24 * 60 * 60 * 1000

export const Route = createFileRoute('/events-calendar')({
  validateSearch: parseSearch,
  component: RouteComponent,
})

function RouteComponent() {
  const search = Route.useSearch()
  const now = React.useMemo(() => new Date(), [])
  const from = React.useMemo(() => new Date(now.getTime() - 365 * MS_PER_DAY).toISOString(), [now])
  const to = React.useMemo(() => new Date(now.getTime() + 2 * MS_PER_DAY).toISOString(), [now])

  return (
    <MapRouteShell routePath={Route.fullPath} search={search} timeFrom={from} timeTo={to}>
      <StormCalendarPage />
    </MapRouteShell>
  )
}

function StormCalendarPage() {
  const [selectedEvent, setSelectedEvent] = React.useState<CalendarEventItem | null>(null)
  const { pastEvents, stormSummaries } = useStormEventData({ excludeFuturePastEvents: true })

  const stormItems = React.useMemo<CalendarEventItem[]>(() => {
    return stormSummaries.map((event) => {
      const [r, g, b] = depthToColor(event.maxDepthInches ?? 0)
      return {
        id: event.id,
        title: `${event.sensorEventCount} flood${event.sensorEventCount !== 1 ? 's' : ''} - max ${Math.round(event.maxDepthInches ?? 0)}"`,
        startTime: event.startTime,
        endTime: event.endTime,
        color: `rgba(${r}, ${g}, ${b}, 0.92)`,
        kind: 'storm',
        maxDepthInches: event.maxDepthInches ?? 0,
        sensorEventCount: event.sensorEventCount,
        sensorCount: event.sensorCount,
        metadata: {
          cityEventId: event.cityEventId,
          sensorEvents: event.sensorEvents,
        },
      }
    })
  }, [stormSummaries])

  const calendarItems = React.useMemo<CalendarEventItem[]>(() => {
    return stormItems
  }, [stormItems])

  const panelRange = React.useMemo(() => {
    const allEvents = pastEvents.features || []
    if (!allEvents.length) {
      const now = new Date()
      return {
        fromTime: new Date(now.getTime() - 30 * MS_PER_DAY),
        toTime: new Date(now.getTime() + MS_PER_DAY),
      }
    }

    const minStart = allEvents.reduce(
      (acc, feature) => Math.min(acc, feature.properties.start_time.getTime()),
      Number.POSITIVE_INFINITY,
    )
    const maxEnd = allEvents.reduce(
      (acc, feature) => Math.max(acc, feature.properties.end_time.getTime()),
      Number.NEGATIVE_INFINITY,
    )

    return {
      fromTime: new Date(minStart - MS_PER_DAY),
      toTime: new Date(maxEnd + MS_PER_DAY),
    }
  }, [pastEvents.features])

  const selectedRange = React.useMemo(() => {
    if (!selectedEvent) return panelRange
    const start = new Date(selectedEvent.startTime).getTime()
    const end = new Date(selectedEvent.endTime ?? selectedEvent.startTime).getTime()
    const duration = Math.max(end - start, 60 * 60 * 1000)
    const pad = Math.max(3 * 60 * 60 * 1000, Math.round(duration * 0.35))
    return {
      fromTime: new Date(start - pad),
      toTime: new Date(end + pad),
    }
  }, [panelRange, selectedEvent])

  const selectedMetadata = selectedEvent?.metadata as StormEventMetadata | undefined

  return (
    <Box sx={{ p: 1.5, height: '100dvh', boxSizing: 'border-box' }}>
      <Paper sx={{ height: '100%', p: 1.25 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} sx={{ height: '100%', minHeight: 0 }}>
          <Paper
            sx={{
              width: { xs: '100%', md: 360 },
              minWidth: { md: 360 },
              maxWidth: { md: 360 },
              p: 1,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'rgba(255,255,255,0.04)',
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              minHeight: 0,
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {selectedEvent ? selectedEvent.title : 'Select a storm event'}
              </Typography>
              {selectedEvent ? (
                <Button size="small" onClick={() => setSelectedEvent(null)}>
                  Clear
                </Button>
              ) : null}
            </Stack>

            {selectedEvent ? (
              <Box sx={{ minHeight: 0, overflow: 'auto' }}>
                <Typography variant="caption" display="block">
                  Start: {format(new Date(selectedEvent.startTime), 'PPpp')}
                </Typography>
                <Typography variant="caption" display="block" sx={{ mb: 0.65 }}>
                  End: {format(new Date(selectedEvent.endTime), 'PPpp')}
                </Typography>
                <Typography variant="body2">Type: {selectedEvent.kind}</Typography>
                {selectedEvent.sensorCount != null ? (
                  <Typography variant="body2">Sensors involved: {selectedEvent.sensorCount}</Typography>
                ) : null}
                {selectedEvent.sensorEventCount != null ? (
                  <Typography variant="body2">Sensor events: {selectedEvent.sensorEventCount}</Typography>
                ) : null}
                {selectedEvent.maxDepthInches != null ? (
                  <Typography variant="body2" sx={{ mb: 0.8 }}>
                    Max depth: {selectedEvent.maxDepthInches.toFixed(2)} in
                  </Typography>
                ) : null}

                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.45 }}>
                  Per-sensor events
                </Typography>
                <Stack spacing={0.5} sx={{ maxHeight: 210, overflow: 'auto', pr: 0.5 }}>
                  {(selectedMetadata?.sensorEvents || []).map((sensorEvent) => (
                    <Paper
                      key={`sensor-event-${sensorEvent.id}`}
                      sx={{
                        p: 0.8,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1.5,
                        bgcolor: 'rgba(255,255,255,0.04)',
                      }}
                    >
                      <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>
                        {sensorEvent.name || sensorEvent.deploymentId}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {sensorEvent.deploymentId}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {format(sensorEvent.startTime, 'Pp')} to {format(sensorEvent.endTime, 'Pp')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        Max depth: {sensorEvent.maxDepthInches.toFixed(2)} in
                      </Typography>
                    </Paper>
                  ))}
                </Stack>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Click a storm event in the timeline to inspect the storm and its per-sensor events.
              </Typography>
            )}

            <Box sx={{ mt: 'auto' }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Timeline overview {selectedEvent ? '(zoomed to selection)' : ''}
              </Typography>
              <MiniMapPanel height={64} fromTime={selectedRange.fromTime} toTime={selectedRange.toTime} />
            </Box>
          </Paper>

          <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <GlassTimelineCalendar
              events={calendarItems}
              initialView="month"
              yearsToShow={8}
              monthsToShow={24}
              weeksToShow={52}
              weekStartsOn={0}
              showPopoverOnEventClick={false}
              onEventClick={(event) => setSelectedEvent(event as CalendarEventItem)}
            />
          </Box>
        </Stack>
      </Paper>
    </Box>
  )
}
